import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "~/components/PageHeader";
import { PinPlusIcon } from "~/icons/PinPlusIcon";
import { getUserLocations } from "~/lib/server-functions/get-user-locations";
import { LocationCard } from "~/components/Location/LocationCard";
import { LocationWithImages } from "~/lib/server-functions/get-locations";
import noImageFound from "~/assets/no-image.svg";
import { EditLocationIcon } from "~/icons/EditLocation";
import { ChevronRightIcon } from "~/icons/ChevronRightIcon";
import { cn, resolveAssetUrl } from "~/lib/utils";
import { getLocationPath } from "~/lib/utils/location-path";
import { withLocale } from "~/lib/utils/with-locale";

export const Route = createFileRoute("/advertiser/_main/locations")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: locations = [], isLoading } = useQuery({
    queryKey: ["user-locations"],
    queryFn: async () => {
      return await getUserLocations({ data: undefined });
    },
  });

  return (
    <div className="w-full mb-6">
      <PageHeader title="Meine Locations" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-4 md:gap-y-12 mb-6">
        <Link
          to="/advertiser/add-location"
          className="bg-neutral-lighter hover:bg-neutral-lightest border border-neutral-lighter text-neutral-darker text-sm aspect-video flex flex-col gap-3 justify-center items-center"
        >
          <PinPlusIcon className="w-12 h-12" />
          Neue Location hinzufügen
        </Link>
        {locations.map((location) => (
          <Location key={location.id} location={location} />
        ))}
      </div>
    </div>
  );
}

const Location = ({ location }: { location: LocationWithImages }) => {
  const images = location.images.filter(
    (image) =>
      image.purpose !== "location_logo" &&
      image.purpose !== "location_contact_person_image"
  );

  const image = images[0];

  return (
    <div>
      <div className="aspect-video relative">
        <img
          src={image?.path ? resolveAssetUrl(image.path) : noImageFound}
          alt={image?.alt || ""}
          className="w-full h-full object-cover"
        />
        <span className={cn("absolute bottom-0 left-0 text-sm px-2 w-28 py-1 text-center", location.status === "draft" ? "bg-white-dark" : "bg-primary")}>
          {location.status === "draft" ? "Entwurf" : "Online"}
        </span>
      </div>
      <h2 className="mt-4 text-xl font-semibold">{location.name}</h2>
      <div className="flex items-center gap-6">
        <Link
          to="/advertiser/edit-location/$id"
          params={{ id: location.id.toString() }}
          className="font-semibold text-sm mt-8 inline-flex items-center gap-1 hover:underline"
        >
          Bearbeiten
          <EditLocationIcon className="size-5" />
        </Link>
        <a
          href={`${import.meta.env.VITE_BASE_URL}${withLocale(getLocationPath(location))}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-sm mt-8 inline-flex items-center gap-1 hover:underline"
        >
          Vorschau
          <ChevronRightIcon className="size-4" />
        </a>
      </div>
    </div >
  );
};
